import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Home.css';
import API from '../api/api';
import Navigation from '../components/Navigation';
import { getMyCertificates, getMyEnrolments, getMyEntries } from '../api/testSystem';
import { canOpenCycle, canOpenTrial, trialHref } from '../lib/trialDoor';
import { cycleScheduleLine } from '../utils/testCycle';
import { formatInTimeZone, formatToLocalDate, getTimeZoneLabel, parseUTCToLocal } from '../utils/dateUtils';
import { isAuthenticated } from '../utils/auth';
import { monogramFor, resolveBrandKit } from '../utils/brandKit';
import { useTrialBrandLogo } from '../utils/brandLogo';

/*
 * The Appli-level home, and the boundary between the two worlds a candidate can
 * be in: roles they were invited to, and certificates they are working toward.
 * Both are listed, one under the other, and never blended — the hiring section
 * is the employer's business, the certificates section is Appli's.
 *
 * The page reads top-down in the order a student needs it: what is open for
 * them right now (a ledger of deadlines, soonest first), then the employer's
 * world — work trials and interviews side by side, so neither is the one under
 * the fold — then Appli's: cycles, entries and what they issued. A world with
 * nothing in it stands aside, below the one that has.
 *
 * This page stays Appli chrome. The workspace's brand layer (`--ws-*`,
 * `data-brand`, `brand-tokens.css`) is deliberately absent: an employer's colours
 * belong inside that employer's workspace and nowhere else. What crosses the
 * boundary from `brand_kit_snapshot` is identity only — a name and, when one was
 * uploaded, a mark. Everything here is drawn from `styles/tokens.css`.
 *
 * Every selector is `home-` prefixed. Page CSS in this app is global, so the
 * unprefixed names this file used to own (`.tab-button`, `.spinner`,
 * `.empty-state`, ...) were reachable from every other page in the bundle.
 */

/** The portal states what happened and when. It never grades. */
const STAGE_ORDER = { new: 0, viewed: 1, 'in-progress': 2, completed: 3, closed: 4 };

const OPEN_STAGES = ['new', 'viewed', 'in-progress'];

const DAY_MS = 24 * 60 * 60 * 1000;

/* "Up next" folds past its first few rows — but only when folding puts away
   enough to be worth a click: a list one or two over the limit is shown whole. */
const NEXT_LIMIT = 5;
const NEXT_SLACK = 2;

/**
 * A deadline that has passed closes the invitation. That is a fact about the
 * window, not a verdict about the person, so it is stated as a date under a
 * soft label and counted in its own bucket.
 */
const interviewStage = (interview) => {
    if (interview.submission_date) return 'completed';
    const deadline = parseUTCToLocal(interview.deadline);
    if (deadline && deadline < new Date()) return 'closed';
    if (interview.start_date) return 'in-progress';
    if (interview.viewed_date) return 'viewed';
    return 'new';
};

const isOpenStage = (stage) => OPEN_STAGES.includes(stage);

const interviewBadge = (stage) => {
    if (stage === 'completed') return { label: 'Completed', tone: 'done' };
    if (stage === 'closed') return { label: 'Closed', tone: 'quiet' };
    return { label: 'Active', tone: 'open' };
};

const interviewDateText = (interview, stage) => {
    if (stage === 'completed') return `Completed ${formatToLocalDate(interview.submission_date)}`;
    if (stage === 'closed') return `Closed ${formatToLocalDate(interview.deadline)}`;
    return interview.deadline ? `Due ${formatToLocalDate(interview.deadline)}` : 'No deadline';
};

/**
 * A trial's state comes from its own two dates. `work-trials/get-all` serves the
 * candidate-safe allow-list, which carries `started_at` / `ends_at` and no
 * status field, so the dates are the whole story.
 */
const trialState = (trial) => {
    const now = new Date();
    const ends = parseUTCToLocal(trial.ends_at);
    if (trial.banned || (ends && ends < now)) return 'closed';
    const started = parseUTCToLocal(trial.started_at);
    if (!started || started > now) return 'upcoming';
    return 'open';
};

const TRIAL_BADGES = {
    open: { label: 'In progress', tone: 'open' },
    upcoming: { label: 'Not started', tone: 'quiet' },
    closed: { label: 'Closed', tone: 'quiet' },
};

const trialDateText = (trial, state) => {
    if (state === 'upcoming') {
        return trial.started_at ? `Starts ${formatToLocalDate(trial.started_at)}` : 'Start date to come';
    }
    if (state === 'closed') {
        return trial.ends_at ? `Ended ${formatToLocalDate(trial.ends_at)}` : `Started ${formatToLocalDate(trial.started_at)}`;
    }
    return trial.ends_at ? `Ends ${formatToLocalDate(trial.ends_at)}` : `Started ${formatToLocalDate(trial.started_at)}`;
};

/**
 * Identity, not livery. The real employer name wins when the portal knows it;
 * the kit's wordmark is the fallback for house and certificate kits, which is
 * where the fictional house names legitimately belong.
 */
const trialIdentity = (trial) => {
    const kit = trial.brand_kit_snapshot ? resolveBrandKit(trial.brand_kit_snapshot) : null;
    const name = (trial.company || kit?.wordmarkText || '').trim();
    // `logoPath` is not a URL and never was — the bucket is private. It says only whether
    // this trial has a mark worth leasing; `TrialDoor` does the leasing.
    return { name, hasLogo: Boolean(kit?.logoPath), monogram: monogramFor(name) };
};

/* Cycle status in one word, for a summary row. The full standing — confirmation
   windows, returned entries — is the enrolments page's job, and it is linked. */
const CYCLE_STATES = {
    open: { label: 'Open for enrolment', tone: 'open' },
    confirmation: { label: 'Confirmation window', tone: 'attention' },
    running: { label: 'Running', tone: 'open' },
    ended: { label: 'Ended', tone: 'quiet' },
    cancelled: { label: 'Cancelled', tone: 'quiet' },
    archived: { label: 'Archived', tone: 'quiet' },
    draft: { label: 'Draft', tone: 'quiet' },
};

/**
 * Where the candidate stands on a cohort, which is not the cohort's status. A
 * cycle whose confirmation window closed on someone reads `running` for everyone,
 * including the person whose spot it released — this row said "Running" to a
 * student who had lost her place and explained nothing. Their own standing wins
 * the chip whenever it says something the cycle's status does not.
 *
 * The words are the ones My enrolments uses for the same two states.
 */
const STANDING = {
    no_show: {
        label: 'Spot released',
        tone: 'quiet',
        note: 'You did not confirm in time, so your spot was released and your entry '
            + 'went back to your balance.',
    },
    cancelled_credited: {
        label: 'Entry returned',
        tone: 'quiet',
        note: 'This cycle was cancelled. Your entry went back to your balance.',
    },
};

const cycleMoment = (at, timeZone) => {
    if (!at) return '';
    const label = getTimeZoneLabel(at, timeZone);
    return `${formatInTimeZone(at, timeZone)}${label ? ` ${label}` : ''}`;
};

/** A confirmation window that is open on a spot this candidate has not confirmed. */
const needsConfirming = (row) => row.cycle?.status === 'confirmation'
    && row.enrolment?.status === 'enrolled';

/**
 * A moment as the ledger's left column prints it: the day on one line, the clock
 * on the next, on the cohort's own zone when the row belongs to a cohort and on
 * the student's otherwise — the same rule every other time on this page follows.
 */
const ledgerMoment = (at, timeZone) => {
    const date = parseUTCToLocal(at);
    if (!date) return null;
    const dayOptions = { weekday: 'short', month: 'short', day: 'numeric' };
    const timeOptions = { hour: '2-digit', minute: '2-digit' };
    let day;
    let time;
    try {
        day = date.toLocaleDateString(undefined, { ...dayOptions, timeZone });
        time = date.toLocaleTimeString(undefined, { ...timeOptions, timeZone });
    } catch (error) {
        // An unknown zone name throws; the student's own clock is better than nothing.
        day = date.toLocaleDateString(undefined, dayOptions);
        time = date.toLocaleTimeString(undefined, timeOptions);
    }
    const zone = getTimeZoneLabel(at, timeZone);
    return { day, time: zone ? `${time} ${zone}` : time, ms: date.getTime() };
};

/* One dead endpoint must not cost the candidate the rest of the page. */
const sideRead = (what) => (error) => {
    console.error(`Home: could not load ${what}`, error);
    return null;
};

const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

const ChevronIcon = ({ className }) => (
    <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 18l6-6-6-6" />
    </svg>
);

/* Nothing to list is an ordinary state, not a fault, so it is not drawn as a box
   with a hole in it: it is a sentence set beside a rule, on the page's own
   surface, with the way forward in it when there is one. `tone="attention"` is
   for the one case that is a fault — a read that did not come back. */
const Empty = ({ title, tone, children }) => (
    <div className={`home-empty${tone ? ` home-empty--${tone}` : ''}`}>
        <p className="home-empty-title">{title}</p>
        <p className="home-empty-text">{children}</p>
    </div>
);

const Loading = ({ label }) => (
    <div className="home-loading" role="status">
        <span className="home-spinner" aria-hidden="true" />
        <span>{label}</span>
    </div>
);

const Badge = ({ tone, children }) => (
    <span className={`home-chip home-chip--${tone}`}>{children}</span>
);

/**
 * The door onto one trial: who it is with, when it runs, and the way in.
 *
 * The mark is leased the same way the workspace inside leases it, and by the same store,
 * so opening a trial from here does not re-ask for a URL this card already holds. This
 * card used to point an `<img>` straight at a `storage.googleapis.com` URL built from the
 * kit's private path, which 403s — a branded trial showed a broken image here, where the
 * workspace at least fell back to a glyph. The monogram beneath is the fallback, and it is
 * what a trial with no mark, an unfetchable one, or no kit at all has always shown.
 */
const TrialDoor = ({ trial }) => {
    const state = trialState(trial);
    const badge = TRIAL_BADGES[state];
    const identity = trialIdentity(trial);
    const logo = useTrialBrandLogo(trial.work_trial_id, identity.hasLogo);

    return (
        <article className="home-door">
            <div className="home-door-brand">
                <span className="home-door-mark" aria-hidden="true">
                    {logo.url
                        ? (
                            <img
                                key={logo.attempt}
                                className="home-door-logo"
                                src={logo.url}
                                alt=""
                                onError={logo.onImageError}
                            />
                        )
                        : identity.monogram}
                </span>
                <span className="home-door-org">{identity.name || 'Employer not named'}</span>
            </div>
            <h4 className="home-door-title">{trial.title || 'Work trial'}</h4>
            <p className="home-door-when">{trialDateText(trial, state)}</p>
            <div className="home-door-foot">
                <Badge tone={badge.tone}>{badge.label}</Badge>
                <Link className="home-door-go" to={trialHref(trial)}>
                    Enter workspace
                    <ChevronIcon />
                </Link>
            </div>
        </article>
    );
};

const InterviewGroup = ({ group, expanded, onToggle, onOpen }) => {
    const place = [group.jobPosting.company, group.jobPosting.location].filter(Boolean).join(' · ');
    /* What is still open is said first, and in ink: it is the part of the line
       that is asking for something. */
    const rest = [
        group.done > 0 && `${group.done} completed`,
        group.closed > 0 && `${group.closed} closed`,
    ].filter(Boolean).join(' · ');

    return (
        <div className="home-group">
            <button
                type="button"
                className="home-group-head"
                onClick={onToggle}
                aria-expanded={expanded}
            >
                <ChevronIcon className={`home-group-caret${expanded ? ' home-group-caret--open' : ''}`} />
                <span className="home-group-info">
                    <span className="home-group-title">{group.jobPosting.title}</span>
                    {place && <span className="home-group-place">{place}</span>}
                </span>
                <span className="home-group-tally">
                    {group.active > 0 && (
                        <span className="home-group-tally-open">{group.active} active</span>
                    )}
                    {group.active > 0 && rest && ' · '}
                    {rest}
                    {group.active === 0 && !rest && `${group.total} invited`}
                </span>
            </button>

            {expanded && (
                <div className="home-group-body">
                    <table className="home-table">
                        <thead>
                            <tr>
                                <th>Interview</th>
                                <th>Date</th>
                                <th className="home-col-state">State</th>
                                <th className="home-col-arrow" />
                            </tr>
                        </thead>
                        <tbody>
                            {group.interviews.map((interview) => {
                                const stage = interviewStage(interview);
                                const badge = interviewBadge(stage);
                                const open = isOpenStage(stage);
                                const questions = interview.questions
                                    ? Object.keys(interview.questions).length
                                    : 0;

                                return (
                                    <tr
                                        key={interview.interview_id}
                                        className={`home-row${open ? ' home-row--open' : ''}`}
                                        onClick={(event) => {
                                            // The name is a real link; let it navigate itself
                                            // rather than routing twice to the same place.
                                            if (open && !event.target.closest('a')) onOpen(interview);
                                        }}
                                    >
                                        <td className="home-col-name">
                                            {open
                                                ? (
                                                    <Link
                                                        className="home-row-link"
                                                        to={`/pre-screening-interview/${interview.interview_id}`}
                                                    >
                                                        {interview.interview_name || 'Interview'}
                                                    </Link>
                                                )
                                                : (interview.interview_name || 'Interview')}
                                            {/* How long it is, under what it is: a count
                                                with a column of its own took a quarter of
                                                the row to say one digit. */}
                                            <span className="home-row-sub">{plural(questions, 'question')}</span>
                                        </td>
                                        <td className="home-col-date">{interviewDateText(interview, stage)}</td>
                                        <td className="home-col-state">
                                            <Badge tone={badge.tone}>{badge.label}</Badge>
                                        </td>
                                        <td className="home-col-arrow">
                                            {open && <ChevronIcon className="home-row-arrow" />}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const CycleRow = ({ row }) => {
    const standing = STANDING[row.enrolment?.status];
    const confirming = needsConfirming(row);
    const state = standing
        || (confirming ? { label: 'Confirm your spot', tone: 'attention' } : null)
        || CYCLE_STATES[row.status]
        || { label: row.status || 'Scheduled', tone: 'quiet' };
    /* The same sentence the catalog gives the same cohort, off the same helper:
       a manual-start cycle has no `start_at` for its whole life, so reading that
       field alone left a running cohort with a fragment ("ends …", lower-cased,
       mid-sentence) and a started one with nothing at all. */
    const when = row.cycle
        ? cycleScheduleLine(row.cycle)
        : [
            row.startAt && `Starts ${cycleMoment(row.startAt, row.timeZone)}`,
            row.endsAt && `Ends ${cycleMoment(row.endsAt, row.timeZone)}`,
        ].filter(Boolean).join(' · ');
    // A cohort that is over, or a spot that was released or returned, keeps its box with
    // nothing inside it to open: the cycle's page answers 404 to them.
    const linked = Boolean(row.cycleId) && canOpenCycle(row.enrolment, row.cycle);
    /* A cancelled cohort has no calendar left to state, and the chip already says
       the word — one card never says "Cancelled" twice. */
    const whenLine = when && when !== state.label ? when : '';

    return (
        <article className="home-cycle" data-testid="home-cycle">
            <div className="home-cycle-head">
                <h4 className="home-cycle-title">
                    {linked
                        ? <Link className="home-link" to={`/tests/cycles/${row.cycleId}`}>{row.title}</Link>
                        : row.title}
                </h4>
            </div>
            {whenLine && <p className="home-cycle-when">{whenLine}</p>}
            {standing && <p className="home-cycle-note">{standing.note}</p>}
            {confirming && (
                <p className="home-cycle-note home-cycle-note--attention">
                    {row.cycle?.start_at
                        ? `Confirm by ${cycleMoment(row.cycle.start_at, row.timeZone)} — that is when the cycle starts.`
                        : 'Confirm now. The team closes this window when they start the cycle.'}
                    {' '}A spot that is not confirmed is given up, and the entry goes back to
                    your balance.
                </p>
            )}
            <div className="home-cycle-foot">
                <Badge tone={state.tone}>{state.label}</Badge>
                {row.trial && canOpenTrial(row.enrolment, row.cycle) && (
                    <Link className="home-door-go" to={trialHref(row.trial)}>
                        Enter workspace
                        <ChevronIcon />
                    </Link>
                )}
                {confirming && (
                    /* The one time-critical thing on this page had no way out of it.
                       The button that takes the spot lives on My enrolments, with the
                       full deadline and what missing it costs; this is the way there. */
                    <Link className="home-door-go" to="/tests/enrolments">
                        Confirm participation
                        <ChevronIcon />
                    </Link>
                )}
            </div>
        </article>
    );
};

/**
 * One line of the ledger: when, what, where it stands, and the way in. The left
 * column is the moment — day over clock, in tabular figures — because the rows
 * are ordered by it and that order is the information.
 */
const LedgerRow = ({ row }) => (
    <li
        className={`home-ledger-row${row.soon ? ' home-ledger-row--soon' : ''}`}
        data-testid={row.confirm ? 'confirm-alert' : undefined}
    >
        <div className="home-ledger-when">
            <span className="home-ledger-when-label">{row.whenLabel}</span>
            {row.at ? (
                <>
                    <span className="home-ledger-day">{row.at.day}</span>
                    <span className="home-ledger-time">{row.at.time}</span>
                </>
            ) : (
                <span className="home-ledger-day">{row.whenEmpty}</span>
            )}
        </div>
        <div className="home-ledger-body">
            <span className="home-ledger-kind">{row.kind}</span>
            <span className="home-ledger-title">{row.title}</span>
            {row.org && <span className="home-ledger-org">{row.org}</span>}
            {row.detail && <span className="home-ledger-detail">{row.detail}</span>}
        </div>
        <div className="home-ledger-state">
            <Badge tone={row.chip.tone}>{row.chip.label}</Badge>
        </div>
        {/* Several rows can carry the same words ("Enter workspace"); read out of
            context, each link also says which thing it opens. */}
        <Link
            className="home-door-go home-ledger-go"
            to={row.action.to}
            aria-label={`${row.action.label}: ${row.title}`}
        >
            {row.action.label}
            <ChevronIcon />
        </Link>
    </li>
);

const Home = () => {
    const [trials, setTrials] = useState([]);
    const [interviews, setInterviews] = useState([]);
    const [tests, setTests] = useState({ enrolments: [], entriesBalance: null, certificates: [] });
    const [hiringLoading, setHiringLoading] = useState(true);
    const [hiringFailed, setHiringFailed] = useState(false);
    const [testsLoading, setTestsLoading] = useState(true);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [allNextShown, setAllNextShown] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/login');
            return undefined;
        }

        let live = true;

        const loadHiring = async (userId) => {
            const [trialsResponse, interviewsResponse] = await Promise.all([
                API.get('work-trials/get-all').catch(sideRead('work trials')),
                API.get(`pre-screening-interviews/user-pre-screening-interviews/${userId}`)
                    .catch(sideRead('interviews')),
            ]);
            if (!live) return;
            setTrials(trialsResponse?.data?.work_trials || []);
            setInterviews(interviewsResponse?.data?.interviews || []);
            /* Both reads dead is not the same fact as an empty column, and this
               page used to state the second when it only knew the first. */
            setHiringFailed(!trialsResponse && !interviewsResponse);
            setHiringLoading(false);
        };

        const loadTests = async () => {
            const [enrolments, entries, certificates] = await Promise.all([
                getMyEnrolments().catch(sideRead('enrolments')),
                getMyEntries().catch(sideRead('entries')),
                getMyCertificates().catch(sideRead('certificates')),
            ]);
            if (!live) return;
            setTests({
                enrolments: enrolments || [],
                entriesBalance: typeof entries?.entries_balance === 'number' ? entries.entries_balance : null,
                certificates: certificates || [],
            });
            setTestsLoading(false);
        };

        API.get('users/account-data')
            .then((response) => {
                if (!live) return;
                loadHiring(response.data?.user?.user_id);
                loadTests();
            })
            .catch(() => {
                if (live) navigate('/login');
            });

        return () => { live = false; };
    }, [navigate]);

    const trialsById = useMemo(() => {
        const index = {};
        trials.forEach((trial) => { index[trial.work_trial_id] = trial; });
        return index;
    }, [trials]);

    const query = searchQuery.trim().toLowerCase();

    /* The hiring world: employer-sourced trials only. A cycle trial is the test
       world's own door and is listed there, beside the cycle that opened it. */
    const hiringTrials = useMemo(() => {
        const matches = (trial) => !query
            || `${trial.title || ''} ${trial.company || ''}`.toLowerCase().includes(query);

        return trials
            .filter((trial) => trial.source !== 'cycle' && matches(trial))
            .sort((a, b) => {
                const order = { open: 0, upcoming: 1, closed: 2 };
                const byState = order[trialState(a)] - order[trialState(b)];
                if (byState !== 0) return byState;
                return (parseUTCToLocal(b.started_at) || 0) - (parseUTCToLocal(a.started_at) || 0);
            });
    }, [trials, query]);

    const tallies = useMemo(() => {
        let completed = 0;
        let active = 0;
        let closed = 0;
        interviews.forEach((interview) => {
            const stage = interviewStage(interview);
            if (stage === 'completed') completed += 1;
            else if (stage === 'closed') closed += 1;
            else active += 1;
        });
        const total = interviews.length;
        return {
            total,
            completed,
            active,
            closed,
            completedPct: total ? (completed / total) * 100 : 0,
            activePct: total ? (active / total) * 100 : 0,
            closedPct: total ? (closed / total) * 100 : 0,
        };
    }, [interviews]);

    const interviewGroups = useMemo(() => {
        const filtered = query
            ? interviews.filter((interview) => {
                const posting = interview.job_posting || {};
                return `${posting.title || ''} ${posting.company || ''}`.toLowerCase().includes(query);
            })
            : interviews;

        const grouped = {};
        filtered.forEach((interview) => {
            const postingId = interview.job_posting_id || 'unknown';
            if (!grouped[postingId]) {
                grouped[postingId] = {
                    jobPosting: interview.job_posting
                        || { title: 'Position not specified', company: '', location: '' },
                    interviews: [],
                };
            }
            grouped[postingId].interviews.push(interview);
        });

        return Object.entries(grouped).map(([postingId, data]) => {
            let done = 0;
            let active = 0;
            let closed = 0;
            data.interviews.forEach((interview) => {
                const stage = interviewStage(interview);
                if (stage === 'completed') done += 1;
                else if (stage === 'closed') closed += 1;
                else active += 1;
            });

            const ordered = [...data.interviews].sort(
                (a, b) => (STAGE_ORDER[interviewStage(a)] ?? 5) - (STAGE_ORDER[interviewStage(b)] ?? 5)
            );

            return {
                postingId,
                jobPosting: data.jobPosting,
                interviews: ordered,
                done,
                active,
                closed,
                total: data.interviews.length,
            };
        }).sort((a, b) => {
            if (a.active > 0 && b.active === 0) return -1;
            if (a.active === 0 && b.active > 0) return 1;
            return b.total - a.total;
        });
    }, [interviews, query]);

    /* One row per cycle the candidate stands on, plus any cycle trial whose
       enrolment row did not come back — a running trial is never hidden by a
       side read that failed. */
    const cycleRows = useMemo(() => {
        const seenTrialIds = new Set();
        const rows = tests.enrolments.map((row, index) => {
            const cycle = row.cycle || {};
            const trial = trialsById[row.enrolment?.work_trial_id];
            if (trial) seenTrialIds.add(trial.work_trial_id);
            return {
                key: cycle.cycle_id || row.enrolment?.work_trial_id || `enrolment-${index}`,
                cycleId: cycle.cycle_id || '',
                title: cycle.structure_name || 'Test cycle',
                status: cycle.status,
                startAt: cycle.start_at,
                endsAt: cycle.ends_at,
                timeZone: cycle.display_timezone,
                trial: trial && trialState(trial) !== 'upcoming' ? trial : null,
                enrolment: row.enrolment || null,
                cycle: row.cycle || null,
            };
        });

        trials.forEach((trial) => {
            if (trial.source !== 'cycle' || seenTrialIds.has(trial.work_trial_id)) return;
            const status = trialState(trial) === 'closed' ? 'ended' : 'running';
            rows.push({
                key: trial.work_trial_id,
                cycleId: '',
                title: trial.title || 'Test cycle',
                status,
                startAt: trial.started_at,
                endsAt: trial.ends_at,
                timeZone: undefined,
                trial,
                /* Enough of a cycle for the row to date itself: the trial's own two
                   dates, which are the only ones this path has. */
                cycle: {
                    status,
                    started_at: trial.started_at,
                    ends_at: status === 'running' ? trial.ends_at : undefined,
                    ended_at: status === 'ended' ? trial.ends_at : undefined,
                },
                // A cycle trial is only instantiated for a confirmed enrolment.
                enrolment: { status: 'confirmed', work_trial_id: trial.work_trial_id },
            });
        });

        return rows;
    }, [tests.enrolments, trials, trialsById]);

    const activeCertificates = useMemo(
        () => tests.certificates.filter((certificate) => certificate.status === 'active'),
        [tests.certificates]
    );

    /**
     * What is open for the student right now, soonest deadline first: a spot to
     * confirm, an interview to take, a trial that is running. Everything here is
     * also in its own section below; this is the page saying it first.
     *
     * A spot waiting on a confirmation leads when its window has no clock on it:
     * missing one costs the spot and the entry, which makes it the most
     * time-critical thing this page can carry. Anything else without a date
     * follows what has one.
     */
    const upNext = useMemo(() => {
        const now = Date.now();
        const rows = [];

        cycleRows.forEach((row) => {
            if (needsConfirming(row)) {
                const at = ledgerMoment(row.cycle?.start_at, row.timeZone);
                rows.push({
                    key: `confirm-${row.key}`,
                    confirm: true,
                    kind: 'Cycle',
                    title: row.title,
                    org: '',
                    whenLabel: at ? 'Confirm by' : 'Confirm',
                    whenEmpty: 'Now',
                    at,
                    soon: !at || at.ms - now <= DAY_MS,
                    chip: { label: 'Confirm your spot', tone: 'attention' },
                    detail: 'Confirm your spot to keep it — a spot that is not confirmed is '
                        + 'given up, and the entry goes back to your balance.',
                    action: { to: '/tests/enrolments', label: 'Confirm now' },
                });
            } else if (row.trial && canOpenTrial(row.enrolment, row.cycle)) {
                const at = ledgerMoment(row.endsAt, row.timeZone);
                rows.push({
                    key: `cycle-${row.key}`,
                    kind: 'Cycle',
                    title: row.title,
                    org: '',
                    whenLabel: at ? 'Ends' : 'Cycle',
                    whenEmpty: 'Running now',
                    at,
                    soon: Boolean(at) && at.ms - now <= DAY_MS,
                    chip: { label: 'Running', tone: 'open' },
                    detail: '',
                    action: { to: trialHref(row.trial), label: 'Enter workspace' },
                });
            }
        });

        trials.forEach((trial) => {
            if (trial.source === 'cycle' || trialState(trial) !== 'open') return;
            const at = ledgerMoment(trial.ends_at);
            rows.push({
                key: `trial-${trial.work_trial_id}`,
                kind: 'Work trial',
                title: trial.title || 'Work trial',
                org: trialIdentity(trial).name,
                whenLabel: at ? 'Ends' : 'Work trial',
                whenEmpty: 'Open now',
                at,
                soon: Boolean(at) && at.ms - now <= DAY_MS,
                chip: TRIAL_BADGES.open,
                detail: '',
                action: { to: trialHref(trial), label: 'Enter workspace' },
            });
        });

        interviews.forEach((interview) => {
            if (!isOpenStage(interviewStage(interview))) return;
            const posting = interview.job_posting || {};
            const at = ledgerMoment(interview.deadline);
            const soon = Boolean(at) && at.ms - now <= DAY_MS;
            const count = interview.questions ? Object.keys(interview.questions).length : 0;
            rows.push({
                key: `interview-${interview.interview_id}`,
                kind: 'Interview',
                title: interview.interview_name || 'Interview',
                org: [posting.title, posting.company, count ? plural(count, 'question') : '']
                    .filter(Boolean).join(' · '),
                whenLabel: at ? 'Due' : 'Interview',
                whenEmpty: 'No deadline',
                at,
                soon,
                chip: soon ? { label: 'Due soon', tone: 'attention' } : { label: 'Active', tone: 'open' },
                detail: '',
                action: { to: `/pre-screening-interview/${interview.interview_id}`, label: 'Open interview' },
            });
        });

        const place = (row) => row.at?.ms ?? (row.confirm ? -Infinity : Infinity);
        return rows.sort((a, b) => {
            const first = place(a);
            const second = place(b);
            if (first === second) return 0;
            return first < second ? -1 : 1;
        });
    }, [cycleRows, trials, interviews]);

    /* A long agenda would push the sections it summarises off the first screen,
       which is the problem it exists to solve. Past the first few it folds, and
       says how many more there are. */
    const nextFolds = upNext.length > NEXT_LIMIT + NEXT_SLACK;
    const shownNext = nextFolds && !allNextShown ? upNext.slice(0, NEXT_LIMIT) : upNext;
    const hiddenNext = upNext.length - shownNext.length;

    const toggleGroup = useCallback((postingId) => {
        setExpandedGroups((previous) => ({ ...previous, [postingId]: !previous[postingId] }));
    }, []);

    const openInterview = useCallback((interview) => {
        navigate(`/pre-screening-interview/${interview.interview_id}`);
    }, [navigate]);

    const testsEmpty = cycleRows.length === 0
        && activeCertificates.length === 0
        && !tests.entriesBalance;

    /* An account an employer has never invited to anything: the whole hiring
       world is empty. For a certificate student that is the normal case, and it
       used to take two thirds of their dashboard — a filter box over two empty
       boxes — while the cohort they are actually on sat in the narrow column.
       Empty, the world states itself once and stands aside, under the one that
       has something in it. */
    const employerTrialCount = trials.filter((trial) => trial.source !== 'cycle').length;
    const hiringEmpty = !hiringLoading
        && !hiringFailed
        && employerTrialCount === 0
        && interviews.length === 0;

    /* What is on the page, as a line of counts beside the title: the student sees
       the whole of it before scrolling — interviews included, however far down
       the page they fall — and each count is the way to its section. */
    const index = (!hiringLoading && !testsLoading) ? [
        employerTrialCount > 0 && {
            id: 'home-trials',
            count: employerTrialCount,
            label: employerTrialCount === 1 ? 'Work trial' : 'Work trials',
        },
        interviews.length > 0 && {
            id: 'home-interviews',
            count: interviews.length,
            label: interviews.length === 1 ? 'Interview' : 'Interviews',
        },
        cycleRows.length > 0 && {
            id: 'home-cycles',
            count: cycleRows.length,
            label: cycleRows.length === 1 ? 'Cycle' : 'Cycles',
        },
        Boolean(tests.entriesBalance) && {
            id: 'home-entries',
            count: tests.entriesBalance,
            label: tests.entriesBalance === 1 ? 'Entry' : 'Entries',
        },
        activeCertificates.length > 0 && {
            id: 'home-certificates',
            count: activeCertificates.length,
            label: activeCertificates.length === 1 ? 'Certificate' : 'Certificates',
        },
    ].filter(Boolean) : [];

    /* The index moves the page, not the address: followed as a plain `#fragment`
       it would be a history entry and a second page view for what is a scroll.
       Focus goes with it, so a keyboard carries on from the section it asked for. */
    const jumpTo = useCallback((event, id) => {
        const target = document.getElementById(id);
        if (!target) return;
        event.preventDefault();
        const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        target.scrollIntoView?.({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
        target.focus?.({ preventScroll: true });
    }, []);

    const hiringWorld = (
        <section className="home-world home-world--hiring" aria-labelledby="home-hiring-title">
            <div className="home-world-head">
                <div className="home-world-heading">
                    <h2 className="home-world-title" id="home-hiring-title">Hiring</h2>
                    <p className="home-world-note">
                        Invitations from employers — work trials and interviews.
                    </p>
                </div>
                {!hiringFailed && !hiringEmpty && (
                    <div className="home-search">
                        <svg className="home-search-icon" width="15" height="15" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                            strokeLinejoin="round" aria-hidden="true">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            type="text"
                            className="home-search-input"
                            placeholder="Filter by role or company"
                            aria-label="Filter hiring by role or company"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                        />
                    </div>
                )}
            </div>

            {hiringFailed ? (
                <Empty tone="attention" title="We could not load this">
                    Your work trials and interviews did not load. Refresh the page
                    to try again — nothing else on this page is affected.
                </Empty>
            ) : hiringEmpty ? (
                <Empty title="No employer invitations">
                    Nothing yet. A work trial or an interview appears here when an
                    employer opens one for you.
                </Empty>
            ) : (
                /* Side by side, at one height on the page: neither list sits under
                   the other, so however many trials there are, the interviews are
                   never the thing below the fold. */
                <div className="home-hiring-grid">
                    <div className="home-block" id="home-trials" tabIndex={-1}>
                        <div className="home-block-head">
                            <h3 className="home-block-title">Work trials</h3>
                            {hiringTrials.length > 0 && (
                                <span className="home-block-count">{hiringTrials.length}</span>
                            )}
                        </div>
                        {hiringLoading ? (
                            <Loading label="Loading your work trials" />
                        ) : hiringTrials.length === 0 ? (
                            <Empty title="No work trials">
                                {query
                                    ? 'No work trial matches that filter.'
                                    : 'Nothing yet. A trial appears here when an employer opens one for you.'}
                            </Empty>
                        ) : (
                            <div className="home-doors">
                                {hiringTrials.map((trial) => (
                                    <TrialDoor key={trial.work_trial_id} trial={trial} />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="home-block" id="home-interviews" tabIndex={-1}>
                        <div className="home-block-head">
                            <h3 className="home-block-title">Interviews</h3>
                            {tallies.total > 0 && (
                                <span className="home-block-count">{tallies.total}</span>
                            )}
                        </div>

                        {hiringLoading ? (
                            <Loading label="Loading your interviews" />
                        ) : interviewGroups.length === 0 ? (
                            <Empty title="No interviews">
                                {query
                                    ? 'No interview matches that filter.'
                                    : 'Nothing yet. Interviews appear here once an employer sends one.'}
                            </Empty>
                        ) : (
                            <div className="home-groups">
                                {/* Where every interview stands, in one line above the
                                    list: what is still open is said first. */}
                                <div className="home-tally">
                                    <span className="home-tally-item">
                                        <span className="home-dot home-dot--open" />
                                        {tallies.active} active
                                    </span>
                                    <span className="home-tally-item">
                                        <span className="home-dot home-dot--done" />
                                        {tallies.completed} completed
                                    </span>
                                    <span className="home-tally-item">
                                        <span className="home-dot home-dot--quiet" />
                                        {tallies.closed} closed
                                    </span>
                                    <span className="home-tally-track" aria-hidden="true">
                                        <span className="home-tally-seg home-tally-seg--open"
                                            style={{ width: `${tallies.activePct}%` }} />
                                        <span className="home-tally-seg home-tally-seg--done"
                                            style={{ width: `${tallies.completedPct}%` }} />
                                        <span className="home-tally-seg home-tally-seg--quiet"
                                            style={{ width: `${tallies.closedPct}%` }} />
                                    </span>
                                </div>
                                {interviewGroups.map((group) => (
                                    <InterviewGroup
                                        key={group.postingId}
                                        group={group}
                                        expanded={!!expandedGroups[group.postingId]}
                                        onToggle={() => toggleGroup(group.postingId)}
                                        onOpen={openInterview}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );

    const testsWorld = (
        <section className="home-world home-world--tests" aria-labelledby="home-tests-title">
            <div className="home-world-head">
                <div className="home-world-heading">
                    <h2 className="home-world-title" id="home-tests-title">Certificates</h2>
                    <p className="home-world-note">
                        Appli&apos;s own certification cycles, your entries, and what they issued.
                    </p>
                </div>
                <Link className="home-world-link" to="/tests">
                    Test catalog
                    <ChevronIcon />
                </Link>
            </div>

            {testsLoading ? (
                <Loading label="Loading your certificates" />
            ) : testsEmpty ? (
                <Empty title="Nothing here yet">
                    Browse the <Link className="home-link" to="/tests">test catalog</Link> to
                    enter a certification cycle.
                </Empty>
            ) : (
                <div className={`home-tests-grid${cycleRows.length === 0 ? ' home-tests-grid--no-cycles' : ''}`}>
                    {cycleRows.length > 0 && (
                        <div className="home-block" id="home-cycles" tabIndex={-1}>
                            <div className="home-block-head">
                                <h3 className="home-block-title">Cycles</h3>
                                <span className="home-block-count">{cycleRows.length}</span>
                                <Link className="home-link" to="/tests/enrolments">My enrolments</Link>
                            </div>
                            <div className="home-cycles">
                                {cycleRows.map((row) => <CycleRow key={row.key} row={row} />)}
                            </div>
                        </div>
                    )}

                    <div className="home-tests-side">
                        <div className="home-block" id="home-entries" tabIndex={-1}>
                            <div className="home-block-head">
                                <h3 className="home-block-title">Entries</h3>
                                <Link className="home-link" to="/tests/entries">Balance and history</Link>
                            </div>
                            <div className="home-panel">
                                <p className="home-figure">
                                    <span className="home-figure-number">{tests.entriesBalance ?? 0}</span>
                                    <span className="home-figure-label">
                                        available entr{tests.entriesBalance === 1 ? 'y' : 'ies'}
                                    </span>
                                </p>
                                <p className="home-note">One entry enrols you on one cycle.</p>
                            </div>
                        </div>

                        <div className="home-block" id="home-certificates" tabIndex={-1}>
                            <div className="home-block-head">
                                <h3 className="home-block-title">Issued certificates</h3>
                                {activeCertificates.length > 0 && (
                                    <span className="home-block-count">{activeCertificates.length}</span>
                                )}
                                <Link className="home-link" to="/tests/certificates">All certificates</Link>
                            </div>
                            {activeCertificates.length === 0 ? (
                                <div className="home-panel">
                                    <p className="home-note">None issued yet.</p>
                                </div>
                            ) : (
                                <ul className="home-certs">
                                    {activeCertificates.slice(0, 3).map((certificate) => (
                                        <li className="home-cert" key={certificate.certificate_id}>
                                            <span className="home-cert-name">
                                                {certificate.structure_name || 'Certificate'}
                                            </span>
                                            <span className="home-cert-when">
                                                Issued {formatToLocalDate(certificate.issued_at)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );

    return (
        <>
            <Navigation />
            <div className="home-page">
                <div className="home-shell">
                    <header className="home-head">
                        <div className="home-head-main">
                            <span className="home-eyebrow">Appli</span>
                            <h1 className="home-title">Your portal</h1>
                            <p className="home-lede">
                                Everything an employer has opened for you, and the Appli
                                certificates you are working toward.
                            </p>
                        </div>
                        {index.length > 0 && (
                            <nav className="home-index" aria-label="On this page">
                                {index.map((item) => (
                                    <a
                                        className="home-index-item"
                                        href={`#${item.id}`}
                                        key={item.id}
                                        onClick={(event) => jumpTo(event, item.id)}
                                    >
                                        <span className="home-index-count">{item.count}</span>
                                        <span className="home-index-label">{item.label}</span>
                                    </a>
                                ))}
                            </nav>
                        )}
                    </header>

                    {upNext.length > 0 && (
                        <section className="home-next" aria-labelledby="home-next-title">
                            <div className="home-next-head">
                                <h2 className="home-next-title" id="home-next-title">Up next</h2>
                                <p className="home-next-note">
                                    {upNext.length === 1
                                        ? 'One thing is open for you.'
                                        : `${upNext.length} things are open for you, soonest first.`}
                                </p>
                            </div>
                            <ol className="home-ledger">
                                {shownNext.map((row) => <LedgerRow key={row.key} row={row} />)}
                            </ol>
                            {nextFolds && (
                                <button
                                    type="button"
                                    className="home-ledger-more"
                                    onClick={() => setAllNextShown((shown) => !shown)}
                                    aria-expanded={allNextShown}
                                >
                                    {allNextShown ? 'Show fewer' : `Show ${hiddenNext} more`}
                                </button>
                            )}
                        </section>
                    )}

                    {/* The world with something in it leads. For a certificate student
                        that is Appli's: no employer has invited them to anything, and
                        the empty statement stands aside at the foot of the page. */}
                    <div className="home-worlds">
                        {hiringEmpty ? (
                            <>
                                {testsWorld}
                                {hiringWorld}
                            </>
                        ) : (
                            <>
                                {hiringWorld}
                                {testsWorld}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Home;
